import type { StatusBadgeTone } from '../ui/StatusBadge';

export const COMPLIANCE_TRUTH_STATES = [
  'not_configured',
  'setup_incomplete',
  'information_incomplete',
  'evaluation_overdue',
  'no_current_issues',
  'issues_requiring_attention',
  'status_unavailable',
] as const;

export type ComplianceTruthState = (typeof COMPLIANCE_TRUTH_STATES)[number];
export type ComplianceTruthSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

export type ComplianceTruthProjection = Readonly<{
  state: ComplianceTruthState;
  secondary_states: ComplianceTruthState[];
  scope: {
    kind: 'portfolio' | 'building';
    development_id: string | null;
    development_count: number;
  };
  coverage_basis: 'current_effective_adopted_rules_and_existing_records';
  coverage: {
    expected: number | null;
    accounted: number;
    examined_rules: number;
    examined_obligations: number;
    percentage: number | null;
  };
  issues: {
    total: number;
    highest_severity: ComplianceTruthSeverity | null;
    by_kind?: Record<string, number>;
  };
  reason_codes: string[];
  calculated_at: string;
  last_reliable_assessment_at: string | null;
  last_required_monitoring_activity_at: string | null;
  disclaimer: 'Operational indication — not legal certification.';
}>;

export type ComplianceTruthPresentation = Readonly<{
  state: ComplianceTruthState;
  label: string;
  description: string;
  tone: StatusBadgeTone;
}>;

const statePresentation: Record<ComplianceTruthState, Omit<ComplianceTruthPresentation, 'state'>> = {
  not_configured: {
    label: 'Not configured',
    description: 'Compliance setup has not been established for this scope.',
    tone: 'unavailable',
  },
  setup_incomplete: {
    label: 'Setup incomplete',
    description: 'Compliance setup is incomplete, so a clear status cannot be established.',
    tone: 'warning',
  },
  information_incomplete: {
    label: 'Information incomplete',
    description: 'Required information is incomplete, so the assessment is not fully established.',
    tone: 'warning',
  },
  evaluation_overdue: {
    label: 'Evaluation overdue',
    description: 'The last reliable evaluation is no longer current.',
    tone: 'warning',
  },
  no_current_issues: {
    label: 'No current issues',
    description: 'The current authoritative assessment found no issues in the established expected set.',
    tone: 'success',
  },
  issues_requiring_attention: {
    label: 'Issues requiring attention',
    description: 'The authoritative assessment contains current issues that require attention.',
    tone: 'danger',
  },
  status_unavailable: {
    label: 'Status unavailable',
    description: 'The authoritative compliance status could not be established.',
    tone: 'unavailable',
  },
};

export function presentComplianceTruth(
  state: ComplianceTruthState | string | null | undefined,
): ComplianceTruthPresentation {
  if (state && Object.prototype.hasOwnProperty.call(statePresentation, state)) {
    const authoritativeState = state as ComplianceTruthState;
    return { state: authoritativeState, ...statePresentation[authoritativeState] };
  }
  return { state: 'status_unavailable', ...statePresentation.status_unavailable };
}

/** Select existing attention/coverage states without recalculating an assessment. */
export function hasComplianceAttention(truth: ComplianceTruthProjection | null | undefined): boolean {
  return presentComplianceTruth(truth?.state).state !== 'no_current_issues' || Boolean(truth?.secondary_states.length);
}

/** Group an existing authoritative result; never derive an assessment from issue counts. */
export function hasKnownComplianceIssues(truth: ComplianceTruthProjection | null | undefined): boolean {
  return presentComplianceTruth(truth?.state).state === 'issues_requiring_attention';
}

function itemCount(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function complianceCoverageText(truth: ComplianceTruthProjection | null | undefined): string {
  if (!truth) return 'Authoritative coverage unavailable';
  const examined = `${itemCount(truth.coverage.examined_rules, 'rule')} and ${itemCount(truth.coverage.examined_obligations, 'obligation')} examined`;
  if (truth.coverage.expected === null) {
    return `Expected set not established · ${itemCount(truth.coverage.accounted, 'item')} accounted · ${examined}`;
  }
  const rawPercentage = truth.coverage.percentage;
  const displayPercentage = rawPercentage === null
    ? null
    : Math.floor(Math.min(
      rawPercentage,
      truth.coverage.accounted < truth.coverage.expected ? 99.9 : 100,
    ) * 10) / 10;
  const percentage = displayPercentage === null ? '' : ` (${displayPercentage}%)`;
  return `${truth.coverage.accounted} of ${truth.coverage.expected} expected items accounted${percentage} · ${examined}`;
}

export function formatComplianceDueState(value: string): string {
  return value === 'Compliant' ? 'Due later' : value;
}

export function complianceEvidenceLabel(input: {
  awaitingReview: number;
  mandatory: number;
  accepted: number;
}): string {
  if (input.awaitingReview > 0) return `${itemCount(input.awaitingReview, 'item')} awaiting review`;
  if (input.mandatory === 0) return 'Mandatory evidence setup not recorded';
  if (input.accepted >= input.mandatory) return 'Evidence accepted';
  return `${itemCount(input.mandatory - input.accepted, 'evidence item')} missing`;
}

/** Compact operating summaries distinguish missing setup from urgent known issues. */
export function dashboardComplianceTone(truth: ComplianceTruthProjection | null | undefined) {
 const presentation=presentComplianceTruth(truth?.state);
 if(['not_configured','setup_incomplete','information_incomplete'].includes(presentation.state))return 'neutral' as const;
 if(presentation.state==='issues_requiring_attention'&&truth?.issues.highest_severity&& !['High','Critical'].includes(truth.issues.highest_severity))return 'warning' as const;
 return presentation.tone;
}

/** Audit F-054 (change #67): say what the known compliance issues are, in operator words. */
const issueKindLabels: Record<string, string> = {
  'obligation-overdue': 'Overdue requirements', 'obligation-evidence-missing': 'Evidence missing', 'evidence-review': 'Evidence awaiting review',
  'evidence-rejected': 'Evidence rejected', 'occurrence-overdue': 'Overdue inspections or services', 'occurrence-evidence': 'Inspection evidence outstanding',
  'occurrence-proposed-finding': 'Findings awaiting review', action: 'Open actions', work: 'Open repairs or work', 'occurrence-verification': 'Awaiting sign-off',
  'occurrence-provider': 'Contractor or appointment to arrange', finding: 'Open findings', exception: 'Workflow problems', approval: 'Approvals pending',
  'applicability-conflict': 'Requirements marked not applicable but still active', 'applicability-facts': 'Building facts changed since a requirement was decided',
};
export function complianceIssueBreakdown(truth: Pick<ComplianceTruthProjection, 'issues'> | null | undefined): string {
  return Object.entries(truth?.issues.by_kind ?? {}).filter(([, count]) => count > 0).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([kind, count]) => `${issueKindLabels[kind] ?? kind.replaceAll('-', ' ')}: ${count}`).join(' · ');
}
